import {
  calculateAutomaticSplit,
  findSpecialFileRule,
  ORDER_COLORS,
  type Order,
  type OrderItem,
} from "../shared/order-domain";
import { orderWriteSchema, type OrderWrite } from "../shared/order-schema";
import { discardFile, saveOrder, updateItemFile, uploadFile } from "./api";
import { isOwner } from "./state";
import {
  bindModalBackdrop,
  closeModal,
  element,
  errorMessage,
  escapeHtml,
  openModal,
  showToast,
  suggestedPickup,
  toLocalInput,
} from "./ui";

interface UploadResult {
  key: string;
  name: string;
}

interface UploadRow extends HTMLDivElement {
  uploadState: "idle" | "uploading" | "done" | "error";
  uploadPromise: Promise<UploadResult> | null;
  uploadResult: UploadResult | null;
  committedKey: string;
}

interface PendingUpload {
  index: number;
  row: UploadRow;
}

export interface OrderFormCallbacks {
  onSaved(order: Order): void;
  onItemUploaded(orderId: number, item: OrderItem): void;
  onUploadsSettled(): Promise<void>;
}

const modal = element<HTMLElement>("modal");
let pickupTouched = false;
let totalPriceTouched = false;
let specialSplitTouched = false;
let formSubmitted = false;

function colorOptions(selected: string | null): string {
  const colors: string[] = [...ORDER_COLORS];
  if (selected && !colors.includes(selected)) colors.unshift(selected);
  const active = selected || ORDER_COLORS[0];
  return colors
    .map(
      (color) =>
        `<option value="${escapeHtml(color)}" ${color === active ? "selected" : ""}>${escapeHtml(color)}</option>`,
    )
    .join("");
}

function addFileRow(item: OrderItem | null): UploadRow {
  const row = document.createElement("div") as UploadRow;
  row.className = "file-row";
  row.dataset.id = item ? String(item.id) : "";
  row.dataset.key = item?.file_key || "";
  row.dataset.name = item?.file_name || "";
  row.committedKey = item?.file_key || "";
  row.uploadState = "idle";
  row.uploadPromise = null;
  row.uploadResult = null;
  const name = item?.file_name ? escapeHtml(item.file_name) : "未选择文件";
  row.innerHTML = `<div class="fr-file"><label class="file-pick"><input type="file" class="fr-fileinput" hidden accept=".3mf,model/3mf" /><span class="file-pick-btn">选择文件</span></label><span class="fr-current" title="${name}">${name}</span><span class="fr-upload-status"></span></div><span class="fr-label">颜色</span><select class="fr-color">${colorOptions(item?.color || null)}</select><span class="fr-label">数量</span><input type="number" class="fr-qty" min="1" step="1" value="${item?.quantity || 1}" /><button type="button" class="fr-remove icon-btn danger" title="移除">✕</button>`;
  element<HTMLElement>("fileRows").appendChild(row);
  return row;
}

function currentFileNames(): string[] {
  return [...document.querySelectorAll<UploadRow>("#fileRows .file-row")].map((row) => {
    const input = row.querySelector<HTMLInputElement>(".fr-fileinput");
    return input?.files?.[0]?.name || row.dataset.name || "";
  });
}

function updateSplitPreview(): void {
  const total = element<HTMLInputElement>("f_total_price").value;
  const automatic = calculateAutomaticSplit(total === "" ? null : Number(total));
  const special = element<HTMLInputElement>("f_split_special").value;
  const preview = element<HTMLInputElement>("f_split_preview");
  preview.value = special
    ? `¥${Number(special).toFixed(2)}（特殊，自动 ¥${automatic.toFixed(2)}）`
    : `¥${automatic.toFixed(2)}`;
  preview.classList.toggle("split-negative", automatic < 0);
}

function applySpecialRule(): void {
  const rule = findSpecialFileRule(currentFileNames());
  if (!totalPriceTouched) {
    element<HTMLInputElement>("f_total_price").value = rule ? String(rule.totalPrice) : "";
  }
  if (!specialSplitTouched) {
    element<HTMLInputElement>("f_split_special").value = rule ? String(rule.split) : "";
  }
  updateSplitPreview();
}

function startUpload(row: UploadRow, file: File): void {
  row.dataset.name = file.name;
  row.dataset.key = "";
  row.uploadState = "uploading";
  row.uploadResult = null;
  const status = row.querySelector<HTMLElement>(".fr-upload-status");
  const current = row.querySelector<HTMLElement>(".fr-current");
  if (current) {
    current.textContent = file.name;
    current.title = file.name;
  }
  if (status) status.innerHTML = '<span class="fr-spin"></span>上传中…';
  row.uploadPromise = uploadFile(file)
    .then((result) => {
      row.uploadState = "done";
      row.uploadResult = result;
      row.dataset.key = result.key;
      row.dataset.name = result.name;
      if (status) status.innerHTML = '<span class="fr-ok">✓ 已上传</span>';
      return result;
    })
    .catch((error: unknown) => {
      row.uploadState = "error";
      row.uploadResult = null;
      if (status) {
        status.innerHTML = `<span class="fr-err">✕ ${escapeHtml(errorMessage(error, "上传失败"))}</span>`;
      }
      throw error;
    });
}

async function discardUncommittedRow(row: UploadRow): Promise<void> {
  const discard = async (key: string) => {
    if (key && key !== row.committedKey) await discardFile(key).catch(() => undefined);
  };
  if (row.uploadResult) await discard(row.uploadResult.key);
  else if (row.uploadPromise) {
    row.uploadPromise.then((result) => discard(result.key)).catch(() => undefined);
  }
}

function collectOrder(): { input: OrderWrite; pending: PendingUpload[] } {
  const items: Array<Record<string, unknown>> = [];
  const pending: PendingUpload[] = [];
  for (const row of document.querySelectorAll<UploadRow>("#fileRows .file-row")) {
    const input = row.querySelector<HTMLInputElement>(".fr-fileinput");
    const hasNewFile = Boolean(input?.files?.[0]);
    let fileKey: string | null = row.dataset.key || null;
    let fileName: string | null = row.dataset.name || null;
    if (hasNewFile && row.uploadState === "uploading") {
      fileKey = null;
      fileName = input?.files?.[0]?.name || fileName;
      pending.push({ index: items.length, row });
    } else if (hasNewFile && row.uploadResult) {
      fileKey = row.uploadResult.key;
      fileName = row.uploadResult.name;
    } else if (hasNewFile && row.uploadState === "error") {
      fileKey = null;
      fileName = input?.files?.[0]?.name || fileName;
    }
    const color = row.querySelector<HTMLSelectElement>(".fr-color")?.value || ORDER_COLORS[0];
    const quantity = row.querySelector<HTMLInputElement>(".fr-qty")?.value || "1";
    if (!fileKey && !fileName && color === ORDER_COLORS[0] && quantity === "1") continue;
    items.push({
      ...(row.dataset.id ? { id: Number(row.dataset.id) } : {}),
      file_key: fileKey,
      file_name: fileName,
      color,
      quantity,
    });
  }
  const raw = {
    order_time: element<HTMLInputElement>("f_order_time").value || null,
    xianyu_id: element<HTMLInputElement>("f_xianyu_id").value,
    pickup_code: element<HTMLInputElement>("f_pickup_code").value,
    pickup_time: element<HTMLInputElement>("f_pickup_time").value,
    total_price: element<HTMLInputElement>("f_total_price").value,
    split_special: element<HTMLInputElement>("f_split_special").value,
    notes: element<HTMLInputElement>("f_notes").value,
    items,
  };
  return { input: orderWriteSchema.parse(raw), pending };
}

export function openOrderForm(order: Order | null): void {
  if (!isOwner()) return;
  formSubmitted = false;
  element<HTMLElement>("modalTitle").textContent = order ? "编辑订单" : "添加订单";
  element<HTMLInputElement>("f_id").value = order ? String(order.id) : "";
  element<HTMLInputElement>("f_order_time").value = toLocalInput(order?.order_time || null);
  element<HTMLInputElement>("f_xianyu_id").value = order?.xianyu_id || "";
  element<HTMLInputElement>("f_pickup_code").value = order?.pickup_code || "";
  element<HTMLInputElement>("f_total_price").value = order?.total_price === null || !order ? "" : String(order.total_price);
  element<HTMLInputElement>("f_split_special").value = order?.split_special === null || !order ? "" : String(order.split_special);
  element<HTMLInputElement>("f_notes").value = order?.notes || "";
  pickupTouched = Boolean(order?.pickup_time);
  totalPriceTouched = order?.total_price !== null && Boolean(order);
  specialSplitTouched = order?.split_special !== null && Boolean(order);
  element<HTMLInputElement>("f_pickup_time").value =
    order?.pickup_time || suggestedPickup(element<HTMLInputElement>("f_order_time").value);
  element<HTMLElement>("fileRows").innerHTML = "";
  const items = order?.items.length ? order.items : [null];
  items.forEach(addFileRow);
  applySpecialRule();
  openModal(modal);
}

export function closeOrderForm(): void {
  closeModal(modal);
  if (!formSubmitted) {
    document
      .querySelectorAll<UploadRow>("#fileRows .file-row")
      .forEach((row) => void discardUncommittedRow(row));
  }
}

export function initOrderForm(callbacks: OrderFormCallbacks): void {
  element<HTMLButtonElement>("closeBtn").addEventListener("click", closeOrderForm);
  element<HTMLButtonElement>("cancelBtn").addEventListener("click", closeOrderForm);
  bindModalBackdrop(modal, closeOrderForm);
  element<HTMLButtonElement>("addFileBtn").addEventListener("click", () => addFileRow(null));
  element<HTMLInputElement>("f_total_price").addEventListener("input", () => {
    totalPriceTouched = true;
    updateSplitPreview();
  });
  element<HTMLInputElement>("f_split_special").addEventListener("input", () => {
    specialSplitTouched = true;
    updateSplitPreview();
  });
  element<HTMLInputElement>("f_order_time").addEventListener("input", (event) => {
    if (!pickupTouched) {
      element<HTMLInputElement>("f_pickup_time").value = suggestedPickup(
        (event.target as HTMLInputElement).value,
      );
    }
  });
  element<HTMLInputElement>("f_pickup_time").addEventListener("input", () => {
    pickupTouched = true;
  });
  const rows = element<HTMLElement>("fileRows");
  rows.addEventListener("change", (event) => {
    const input = event.target instanceof Element
      ? event.target.closest<HTMLInputElement>(".fr-fileinput")
      : null;
    const file = input?.files?.[0];
    const row = input?.closest<UploadRow>(".file-row");
    if (!file || !row) return;
    startUpload(row, file);
    applySpecialRule();
  });
  rows.addEventListener("click", (event) => {
    const remove = event.target instanceof Element ? event.target.closest(".fr-remove") : null;
    const row = remove?.closest<UploadRow>(".file-row");
    if (!row) return;
    void discardUncommittedRow(row);
    const allRows = rows.querySelectorAll<UploadRow>(".file-row");
    if (allRows.length > 1) row.remove();
    else {
      rows.innerHTML = "";
      addFileRow(null);
    }
    applySpecialRule();
  });
  const form = element<HTMLFormElement>("orderForm");
  form.addEventListener("keydown", (event) => {
    if (
      event.key === "Enter" &&
      event.target instanceof HTMLInputElement &&
      event.target.type !== "file"
    ) {
      event.preventDefault();
      event.target.blur();
    }
  });
  form.addEventListener("submit", async (event) => {
    event.preventDefault();
    const button = element<HTMLButtonElement>("saveBtn");
    button.disabled = true;
    button.textContent = "保存中…";
    try {
      const idValue = element<HTMLInputElement>("f_id").value;
      const { input, pending } = collectOrder();
      const saved = await saveOrder(input, idValue ? Number(idValue) : undefined);
      formSubmitted = true;
      closeOrderForm();
      callbacks.onSaved(saved);
      for (const upload of pending) {
        const item = saved.items[upload.index];
        if (!item || !upload.row.uploadPromise) continue;
        upload.row.uploadPromise
          .then((result) => updateItemFile(item.id, result.key, result.name))
          .then((updated) => callbacks.onItemUploaded(saved.id, updated))
          .catch((error: unknown) =>
            showToast(
              `文件「${upload.row.dataset.name || ""}」上传失败：${errorMessage(error, "上传失败")}`,
              true,
            ),
          );
      }
      if (pending.length > 0) {
        Promise.allSettled(
          pending.map((upload) => upload.row.uploadPromise ?? Promise.resolve()),
        ).then(() => callbacks.onUploadsSettled());
      }
      showToast(idValue ? "订单已更新" : "订单已添加");
    } catch (error) {
      showToast(errorMessage(error, "保存失败"), true);
    } finally {
      button.disabled = false;
      button.textContent = "保存";
    }
  });
}
